import { Dropdown } from '@/components'
import { useDebounce } from '@/hooks'
import { MonthsEnum } from '@/types'
import { Refresh } from '@mui/icons-material'
import {
  Stack,
  Box,
  Button,
  IconButton,
  TextField,
  Theme,
  useMediaQuery,
} from '@mui/material'
import { invalidateFiscalNoteListQueries } from '@/api/accounting-dealer'
import { useQueryClient } from '@tanstack/react-query'
import { getYear } from 'date-fns'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export const MIN_BORROWER_SEARCH_LENGTH = 3

const CURRENT_DATE = new Date()
const CURRENT_YEAR = getYear(CURRENT_DATE)

// format months options for filter dropdown and sort by desc
const MONTH_OPTIONS = Object.entries(MonthsEnum)
  .map(([key, value]) => ({
    label: value as string,
    value: key,
  }))
  .slice(0, 12)
  .sort((a, b) => Number(b.value) - Number(a.value))

// format years options for filter dropdown
const YEARS_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map(
  (year) => ({
    label: year.toString(),
    value: year,
  }),
)

type FiscalNotesListFiltersProps = {
  month: string
  year: string
}

export function FiscalNotesListFilters({
  month,
  year,
}: FiscalNotesListFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [borrowerInput, setBorrowerInput] = useState(
    () => searchParams.get('q') ?? '',
  )
  const debouncedBorrower = useDebounce(borrowerInput, 300)

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const queryClient = useQueryClient()

  useEffect(() => {
    const next = debouncedBorrower.trim()
    const nextQuery = next.length >= MIN_BORROWER_SEARCH_LENGTH ? next : ''

    setSearchParams(
      (prev) => {
        const current = prev.get('q') ?? ''
        if (current === nextQuery) return prev

        const params = new URLSearchParams(prev)
        if (nextQuery) params.set('q', nextQuery)
        else params.delete('q')
        return params
      },
      { replace: true },
    )
  }, [debouncedBorrower, setSearchParams])

  const handleFilter = (query: string, value: string | number) => {
    searchParams.set(query, value.toString())
    setSearchParams(searchParams)
  }

  const handleRefetchList = () => {
    invalidateFiscalNoteListQueries(queryClient)
  }

  const borrowerField = (
    <TextField
      value={borrowerInput}
      onChange={(event) => setBorrowerInput(event.target.value)}
      placeholder="Nome ou CPF/CNPJ do tomador"
      size="small"
      slotProps={{
        htmlInput: { 'aria-label': 'Nome ou CPF/CNPJ do tomador' },
      }}
      fullWidth={isMobile}
      sx={isMobile ? undefined : { flex: 1, mx: 1, minWidth: 220 }}
    />
  )

  return (
    <Stack mb={2} spacing={1}>
      <Stack direction="row" alignItems="center">
        <Dropdown
          label={MonthsEnum[Number(month)]}
          options={MONTH_OPTIONS}
          onSelect={(value) => handleFilter('m', value)}
        />

        <Dropdown
          label={year}
          options={YEARS_OPTIONS}
          onSelect={(value) => handleFilter('y', value)}
        />

        {isMobile ? <Box flex={1} /> : borrowerField}

        {isMobile ? (
          <IconButton onClick={handleRefetchList}>
            <Refresh />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={handleRefetchList}
          >
            Atualizar
          </Button>
        )}
      </Stack>

      {isMobile ? borrowerField : null}
    </Stack>
  )
}
